import { IEventLoggingComponentProps, withEventLogging } from '@AppModels/Logging';
import { StyleDeclarationValue, css } from 'aphrodite';
import { inject, observer } from 'mobx-react';
import * as React from 'react';
import { IModalContext, ModalChildComponentContextKey } from '../../../../models';
import { IUserSessionComponentProps, UserSessionViewModelKey } from '../../../../models/AppState';
import { IYouTubeVideoInfo, YouTubeUtils } from '../../../../models/YouTubeUtils';
import { baseStyleSheet } from '../../../styles/styles';
import * as Colors from '../../../styles/colors';
import { LoadingSpinner } from '../../LoadingSpinner';
import { asModalComponent } from '../../Modal';
import { Input } from '../../../components-v2/Input';
import { Button } from '../../../components-v2/Button';
import { styleSheet } from './styles';

interface IProps extends IEventLoggingComponentProps, IUserSessionComponentProps, IModalContext<IYouTubeVideoInfo> {
	className?: string;
	styles?: StyleDeclarationValue[];
}

const _YouTubeChooser: React.FC<IProps> = ({ className, styles, parentModal }) => {
	const [state, setState] = React.useState<{
		isLoading?: boolean;
		pendingUrl?: string;
		videoInfo?: IYouTubeVideoInfo;
		error?: string;
	}>({});

	const validateUrl = React.useCallback((url: string) => {
		if (!url.trim()) {
			setState(prevState => ({
				...prevState,
				videoInfo: undefined,
				error: undefined,
				isLoading: false,
			}));
			return;
		}

		try {
			const validateVideoInfo = YouTubeUtils.getVideoInfo(url);
			if (validateVideoInfo) {
				setState(prevState => ({
					...prevState,
					videoInfo: validateVideoInfo,
					error: undefined,
					isLoading: false,
				}));
			} else {
				setState(prevState => ({
					...prevState,
					videoInfo: undefined,
					error: 'Please enter a valid YouTube URL',
					isLoading: false,
				}));
			}
		} catch {
			setState(prevState => ({
				...prevState,
				videoInfo: undefined,
				error: 'Please enter a valid YouTube URL',
				isLoading: false,
			}));
		}
	}, []);

	const onInputKeyDown = React.useCallback(
		(e: React.KeyboardEvent<HTMLInputElement>) => {
			if (e.keyCode === 13) {
				const { videoInfo: stateVideoInfo } = state;
				if (stateVideoInfo) {
					parentModal?.onRequestClose(stateVideoInfo, false);
				}
			}
		},
		[state, parentModal]
	);

	const onCancelClicked = React.useCallback(() => {
		parentModal?.onRequestClose(null, true);
	}, [parentModal]);

	const onInsertClicked = React.useCallback(() => {
		const { videoInfo: stateVideoInfo } = state;
		if (stateVideoInfo) {
			parentModal?.onRequestClose(stateVideoInfo, false);
		}
	}, [state, parentModal]);

	const onUrlInputChanged = React.useCallback(
		(e: React.ChangeEvent<HTMLInputElement>) => {
			const url = e.target.value;
			setState(prevState => ({
				...prevState,
				pendingUrl: url,
				error: undefined,
			}));

			// Validate and update video info immediately as user types
			validateUrl(url);
		},
		[validateUrl]
	);

	const onUrlInputBlur = React.useCallback(() => {
		// Validation now happens immediately on input change
		// This method can remain for any blur-specific behavior if needed
	}, []);

	const renderUrlInput = () => {
		const { pendingUrl, videoInfo: renderVideoInfo, isLoading, error } = state;
		return (
			<div className={css(baseStyleSheet.verticalStack, styleSheet.search)}>
				<div>Enter YouTube URL</div>
				<Input
					id='youtube-url-input'
					autoComplete='off'
					onChange={onUrlInputChanged}
					onKeyDown={onInputKeyDown}
					onBlur={onUrlInputBlur}
					placeholder='https://www.youtube.com/watch?v=...'
					type='text'
					value={pendingUrl || ''}
					autoFocus={true}
				/>
				{error && <div className={css(styleSheet.error)}>{error}</div>}
				{renderVideoInfo && (
					<div className={css(styleSheet.preview)}>
						<img
							src={renderVideoInfo.thumbnailUrl}
							alt='YouTube video thumbnail'
							className={css(styleSheet.thumbnailPreview)}
						/>
						<div className={css(styleSheet.videoTitle)}>Video preview</div>
					</div>
				)}
				{isLoading && <LoadingSpinner className={css(baseStyleSheet.absoluteCenter)} type='large' />}
			</div>
		);
	};

	const { videoInfo } = state;

	return (
		<div className={`${css(styleSheet.container, ...(styles || []))} youtube-chooser ${className || ''}`}>
			<div className={css(styleSheet.header)}>
				<div className={css(baseStyleSheet.horizontalStack)}>
					<svg xmlns='http://www.w3.org/2000/svg' width='40' height='40' viewBox='0 0 40 40'>
						<g fill='none' fillRule='evenodd'>
							<rect width='40' height='40' fill={Colors.heartRed} rx='6' />
							<path fill='#FFF' d='M16 14v12l10-6z' />
						</g>
					</svg>
					<span>YouTube</span>
				</div>
			</div>
			<div>{renderUrlInput()}</div>
			<div className={css(baseStyleSheet.horizontalStack, styleSheet.footer)}>
				<Button variant='primary' onClick={onInsertClicked} disabled={!videoInfo}>
					Insert
				</Button>
				<Button variant='secondary' onClick={onCancelClicked}>
					Cancel
				</Button>
			</div>
		</div>
	);
};

const YouTubeChooserAsObserver = observer(_YouTubeChooser);
const YouTubeChooserWithContext = inject(
	UserSessionViewModelKey,
	ModalChildComponentContextKey
)(YouTubeChooserAsObserver);
export const YouTubeChooser = withEventLogging(YouTubeChooserWithContext, 'YouTubeChooser');
export const YouTubeChooserModal = asModalComponent(YouTubeChooser, {
	className: `youtube-chooser-modal`,
	shouldCloseOnOverlayClick: false,
	useDefaultHeader: true,
});
